import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Select from '@material-ui/core/Select';
import Switch from '@material-ui/core/Switch';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import GetAppIcon from '@material-ui/icons/GetApp';
import SearchIcon from '@material-ui/icons/Search';
// Components
import App from '../../../components/app';
import ErrorMessage from '../../../components/error-message';
import GlobalLoader from '../../../components/main-loading/global-loader';
import MyLink from '../../../components/mylink';
import FastTabs, { TabsDatas } from '../../../components/tabs/tabs';
// Ressources
import envConfig from '../../../config';
import CoreAPIService from '../../../utils/core-api';
import DateUtils from '../../../utils/dateUtils';
import FormattingUtils from '../../../utils/formattingUtils';
import { createPath, paths } from '../../paths';

type StatsPeriod = 'week' | 'month' | 'trimester';

const PERIODS: StatsPeriod[] = ['week', 'month', 'trimester'];

const PAGE_SIZE = 100;

// Matches the backend cap on an export range; used as "to" when exporting all.
const EXPORT_MAX_ROWS = 50000;

const SEARCH_DEBOUNCE_MS = 400;

type SortField = 'zone' | 'activation_date' | 'last_connection_date' | 'last_disconnection_date' | 'last_ride_date';

type SortDir = 'asc' | 'desc';

interface StatsQuery {
    page: number;
    sortBy: SortField | null;
    sortDir: SortDir;
    zone: string;
    hasRidden: '' | 'true' | 'false';
    search: string;
}

const DEFAULT_QUERY: StatsQuery = {
    page: 1,
    sortBy: null,
    sortDir: 'asc',
    zone: '',
    hasRidden: '',
    search: '',
};

interface DriverStats {
    id: number;
    uuid: string;
    firstname: string | null;
    lastname: string | null;
    phone: string | null;
    creation_date: number;
    control_tower_name: string;
    activation_date: number;
    last_connection_date: number;
    last_disconnection_date: number;
    rejected_rides_count: number;
    last_ride_date: number | null;
    accepted_rides_count: number | null;
    min_driver_price: number | null;
    avg_driver_price: number | null;
    max_driver_price: number | null;
    total_income: number | null;
    trend: 'up' | 'down' | null;
    trend_variation: number | null;
}

interface DriverStatsPayload {
    period: StatsPeriod;
    since: number;
    page: number;
    page_size: number;
    total: number;
    zones: string[];
    drivers: DriverStats[];
}

const buildStatsEndpoint = (period: StatsPeriod, query: StatsQuery, range?: { from: number; to: number }): string => {
    const params = [`period=${period}`, `page=${query.page}`];
    if (query.sortBy) {
        params.push(`sort_by=${query.sortBy}`, `sort_dir=${query.sortDir}`);
    }
    if (query.zone) {
        params.push(`zone=${encodeURIComponent(query.zone)}`);
    }
    if (query.hasRidden) {
        params.push(`has_ridden=${query.hasRidden}`);
    }
    if (query.search) {
        params.push(`search=${encodeURIComponent(query.search)}`);
    }
    if (range) {
        params.push(`from=${range.from}`, `to=${range.to}`);
    }
    return `internal/backoffice/drivers/stats?${params.join('&')}`;
};

const CSV_COLUMNS = [
    'id',
    'firstname',
    'lastname',
    'phone',
    'creation_date',
    'control_tower',
    'activation_date',
    'last_connection_date',
    'last_disconnection_date',
    'rejected_rides',
    'last_ride_date',
    'accepted_rides',
    'avg_driver_price',
    'total_income',
    'trend',
];

const csvEscape = (value: string): string => `"${value.replace(/"/g, '""')}"`;

const csvDate = (ts: number | null): string => (ts ? DateUtils.tsToDate(ts) : '');

const buildCsv = (t: any, drivers: DriverStats[]): string => {
    const header = CSV_COLUMNS.map((key) => csvEscape(t(`secret_stats.columns.${key}`)));
    const rows = drivers.map((d) =>
        [
            d.id,
            d.firstname ?? '',
            d.lastname ?? '',
            d.phone ?? '',
            csvDate(d.creation_date),
            d.control_tower_name ?? '',
            csvDate(d.activation_date),
            csvDate(d.last_connection_date),
            csvDate(d.last_disconnection_date),
            d.rejected_rides_count ?? '',
            csvDate(d.last_ride_date),
            d.accepted_rides_count ?? '',
            d.avg_driver_price ?? '',
            d.total_income ?? '',
            d.trend ?? '',
        ].map((v) => csvEscape(String(v))),
    );
    // BOM so Excel opens the UTF-8 accents correctly; ';' for French locales.
    return `\uFEFF${[header, ...rows].map((row) => row.join(';')).join('\n')}`;
};

const downloadCsv = (csv: string, filename: string): void => {
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
};

const useStyles = makeStyles((theme) => ({
    tableContainer: {
        overflowX: 'auto',
    },
    filtersBar: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        gap: theme.spacing(3),
        padding: theme.spacing(1.5, 2, 2),
        marginBottom: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
    },
    filterControl: {
        width: 200,
    },
    searchControl: {
        width: 280,
    },
    exportField: {
        width: 110,
    },
    exportRangeFields: {
        display: 'flex',
        gap: theme.spacing(2),
        marginTop: theme.spacing(1),
        marginLeft: theme.spacing(4),
    },
    // Display option, visually separated from the data filters
    fullViewToggle: {
        marginLeft: 'auto',
        marginRight: 0,
        marginBottom: theme.spacing(-0.5),
    },
    // Distinguishes the sortable columns from the plain ones
    sortableHeader: {
        '& .MuiTableSortLabel-root': {
            color: theme.palette.secondary.main,
        },
        '& .MuiTableSortLabel-root.MuiTableSortLabel-active': {
            color: theme.palette.secondary.dark,
        },
        '& .MuiTableSortLabel-icon': {
            color: `${theme.palette.secondary.dark} !important`,
        },
    },
    trendUp: {
        color: theme.palette.colors.green,
    },
    trendDown: {
        color: theme.palette.colors.red,
    },
}));

interface StatsPanelProps {
    t: any;
    classes: Record<string, string>;
    datas?: DriverStatsPayload;
    error?: any;
    page: number;
    sortBy: SortField | null;
    sortDir: SortDir;
    fullView: boolean;
    onSort: (column: SortField) => void;
    onPageChange: (newPage: number) => void;
}

const StatsPanel = (props: StatsPanelProps): JSX.Element => {
    const { t, classes, datas, error, page, sortBy, sortDir, fullView, onSort, onPageChange } = props;

    const renderTrend = (driver: DriverStats): JSX.Element => {
        if (!driver.trend) {
            return null;
        }
        const title =
            driver.trend_variation !== null && driver.trend_variation !== undefined
                ? `${(driver.trend_variation * 100).toFixed(0)} %`
                : undefined;
        return driver.trend === 'up' ? (
            <ArrowUpwardIcon fontSize="small" className={classes.trendUp} titleAccess={title} />
        ) : (
            <ArrowDownwardIcon fontSize="small" className={classes.trendDown} titleAccess={title} />
        );
    };

    const renderPrice = (value: number | null): string =>
        value !== null && value !== undefined ? FormattingUtils.price(value, true) : '-';

    const renderSortableHeader = (column: SortField, label: string): JSX.Element => (
        <TableCell className={classes.sortableHeader} sortDirection={sortBy === column ? sortDir : false}>
            <TableSortLabel
                active={sortBy === column}
                direction={sortBy === column ? sortDir : 'asc'}
                onClick={() => onSort(column)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    if (error) {
        return <ErrorMessage payload={error} />;
    }

    if (!datas) {
        return <GlobalLoader center />;
    }

    return (
        <>
            <Typography variant="body2" color="textSecondary" gutterBottom>
                {t('secret_stats.since', { date: DateUtils.tsToDate(datas.since) })}
            </Typography>
            {datas.drivers?.length ? (
                <div className={classes.tableContainer}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('secret_stats.columns.id')}</TableCell>
                                {fullView && <TableCell>{t('secret_stats.columns.firstname')}</TableCell>}
                                {fullView && <TableCell>{t('secret_stats.columns.lastname')}</TableCell>}
                                {fullView && <TableCell>{t('secret_stats.columns.phone')}</TableCell>}
                                <TableCell>{t('secret_stats.columns.creation_date')}</TableCell>
                                {renderSortableHeader('zone', t('secret_stats.columns.control_tower'))}
                                {renderSortableHeader('activation_date', t('secret_stats.columns.activation_date'))}
                                {renderSortableHeader(
                                    'last_connection_date',
                                    t('secret_stats.columns.last_connection_date'),
                                )}
                                {renderSortableHeader(
                                    'last_disconnection_date',
                                    t('secret_stats.columns.last_disconnection_date'),
                                )}
                                <TableCell>{t('secret_stats.columns.rejected_rides')}</TableCell>
                                {renderSortableHeader('last_ride_date', t('secret_stats.columns.last_ride_date'))}
                                <TableCell>{t('secret_stats.columns.accepted_rides')}</TableCell>
                                <TableCell>{t('secret_stats.columns.avg_driver_price')}</TableCell>
                                <TableCell>{t('secret_stats.columns.total_income')}</TableCell>
                                <TableCell>{t('secret_stats.columns.trend')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {datas.drivers.map((driver) => (
                                <TableRow key={driver.uuid || driver.id}>
                                    <TableCell>
                                        <MyLink
                                            color="secondary"
                                            to={createPath(paths.driversDetails, { id: driver.id })}
                                        >
                                            {driver.id}
                                        </MyLink>
                                    </TableCell>
                                    {fullView && <TableCell>{driver.firstname || '-'}</TableCell>}
                                    {fullView && <TableCell>{driver.lastname || '-'}</TableCell>}
                                    {fullView && <TableCell>{driver.phone || '-'}</TableCell>}
                                    <TableCell>{DateUtils.tsToDate(driver.creation_date)}</TableCell>
                                    <TableCell>{driver.control_tower_name || '-'}</TableCell>
                                    <TableCell>{DateUtils.tsToDate(driver.activation_date)}</TableCell>
                                    <TableCell>{DateUtils.tsToDate(driver.last_connection_date)}</TableCell>
                                    <TableCell>{DateUtils.tsToDate(driver.last_disconnection_date)}</TableCell>
                                    <TableCell>{driver.rejected_rides_count ?? '-'}</TableCell>
                                    <TableCell>{DateUtils.tsToDate(driver.last_ride_date)}</TableCell>
                                    <TableCell>{driver.accepted_rides_count ?? '-'}</TableCell>
                                    <TableCell>{renderPrice(driver.avg_driver_price)}</TableCell>
                                    <TableCell>{renderPrice(driver.total_income)}</TableCell>
                                    <TableCell>{renderTrend(driver)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        rowsPerPage={PAGE_SIZE}
                        rowsPerPageOptions={[PAGE_SIZE]}
                        count={datas.total}
                        page={page - 1}
                        labelDisplayedRows={({ from, to, count }) =>
                            `${from}-${to} ${t('secret_stats.pagination.of')} ${count}`
                        }
                        onPageChange={(event, newPage) => onPageChange(newPage + 1)}
                    />
                </div>
            ) : (
                <Typography variant="body2">{t('secret_stats.empty')}</Typography>
            )}
        </>
    );
};

interface DriverStatsPageProps extends WithTranslation {}

const DriverStatsPage = ({ t }: DriverStatsPageProps): JSX.Element => {
    const classes = useStyles();

    const [statsByPeriod, setStatsByPeriod] = React.useState<Partial<Record<StatsPeriod, DriverStatsPayload>>>({});
    const [errorsByPeriod, setErrorsByPeriod] = React.useState<Partial<Record<StatsPeriod, any>>>({});
    const [query, setQuery] = React.useState<StatsQuery>(DEFAULT_QUERY);
    // Display-only toggle: full view adds the identity columns, no refetch needed.
    const [fullView, setFullView] = React.useState<boolean>(false);
    // Raw search input, debounced into query.search.
    const [searchInput, setSearchInput] = React.useState<string>('');
    // The tab whose period the CSV export applies to.
    const [activeTab, setActiveTab] = React.useState<number>(0);
    // Export dialog: quantity choice (everything, or rows n to m).
    const [exportOpen, setExportOpen] = React.useState<boolean>(false);
    const [exportMode, setExportMode] = React.useState<'all' | 'range'>('all');
    const [exportFrom, setExportFrom] = React.useState<string>('1');
    const [exportTo, setExportTo] = React.useState<string>('');
    const [exporting, setExporting] = React.useState<boolean>(false);
    // Last non-empty zones list, kept so the filter dropdown does not flicker while loading.
    const [zones, setZones] = React.useState<string[]>([]);
    // Guards against out-of-order responses when the query changes while requests are in flight.
    const requestIdRef = React.useRef(0);

    // All three periods are refetched on every query change; each tab shows its spinner
    // until its own payload arrives (privapi serves them from its memory cache).
    React.useEffect(() => {
        requestIdRef.current += 1;
        const requestId = requestIdRef.current;
        setStatsByPeriod({});
        setErrorsByPeriod({});
        PERIODS.forEach((period) => {
            CoreAPIService.get(buildStatsEndpoint(period, query), null, null, envConfig.privapi_url)
                .then((res: any) => {
                    if (requestId !== requestIdRef.current) {
                        return;
                    }
                    setStatsByPeriod((prev) => ({ ...prev, [period]: res.payload }));
                    if (res.payload?.zones?.length) {
                        setZones(res.payload.zones);
                    }
                })
                .catch((err) => {
                    if (requestId !== requestIdRef.current) {
                        return;
                    }
                    setErrorsByPeriod((prev) => ({ ...prev, [period]: err }));
                });
        });
    }, [query]);

    const handleSort = (column: SortField): void => {
        setQuery((prev) => ({
            ...prev,
            page: 1,
            sortBy: column,
            sortDir: prev.sortBy === column && prev.sortDir === 'asc' ? 'desc' : 'asc',
        }));
    };

    const handlePageChange = (newPage: number): void => {
        setQuery((prev) => ({ ...prev, page: newPage }));
    };

    const handleZoneChange = (event: React.ChangeEvent<{ value: unknown }>): void => {
        const zone = event.target.value as string;
        setQuery((prev) => ({ ...prev, page: 1, zone }));
    };

    const handleHasRiddenChange = (event: React.ChangeEvent<{ value: unknown }>): void => {
        const hasRidden = event.target.value as StatsQuery['hasRidden'];
        setQuery((prev) => ({ ...prev, page: 1, hasRidden }));
    };

    React.useEffect(() => {
        const handle = setTimeout(() => {
            const search = searchInput.trim();
            setQuery((prev) => (prev.search === search ? prev : { ...prev, page: 1, search }));
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [searchInput]);

    const exportRange = (): { from: number; to: number } | null => {
        if (exportMode === 'all') {
            return { from: 1, to: EXPORT_MAX_ROWS };
        }
        const from = parseInt(exportFrom, 10);
        const to = parseInt(exportTo, 10);
        return from >= 1 && to >= from ? { from, to } : null;
    };

    // CSV export of the active tab's period, honoring the current filters and sort.
    const handleExport = (): void => {
        const period = PERIODS[activeTab];
        const range = exportRange();
        if (!range) {
            return;
        }
        setExporting(true);
        CoreAPIService.get(buildStatsEndpoint(period, query, range), null, null, envConfig.privapi_url)
            .then((res: any) => {
                const drivers: DriverStats[] = res.payload?.drivers ?? [];
                const suffix = exportMode === 'all' ? 'all' : `${range.from}-${range.to}`;
                downloadCsv(buildCsv(t, drivers), `driver-stats-${period}-${suffix}.csv`);
                setExportOpen(false);
            })
            .catch((err) => {
                setErrorsByPeriod((prev) => ({ ...prev, [period]: err }));
                setExportOpen(false);
            })
            .finally(() => {
                setExporting(false);
            });
    };

    const tabs: TabsDatas = {
        datas: PERIODS.map((period) => ({
            label: t(`secret_stats.tabs.${period}`),
            children: (
                <StatsPanel
                    t={t}
                    classes={classes}
                    datas={statsByPeriod[period]}
                    error={errorsByPeriod[period]}
                    page={query.page}
                    sortBy={query.sortBy}
                    sortDir={query.sortDir}
                    fullView={fullView}
                    onSort={handleSort}
                    onPageChange={handlePageChange}
                />
            ),
        })),
    };

    return (
        <App
            header={{
                title: t('secret_stats.title'),
                subtitle: t('secret_stats.description'),
            }}
        >
            <div className={classes.filtersBar}>
                <FormControl className={classes.filterControl}>
                    <InputLabel id="driver-stats-zone-label">{t('secret_stats.filters.zone')}</InputLabel>
                    <Select labelId="driver-stats-zone-label" value={query.zone} onChange={handleZoneChange}>
                        <MenuItem value="">{t('secret_stats.filters.zone_all')}</MenuItem>
                        {zones.map((zone) => (
                            <MenuItem key={zone} value={zone}>
                                {zone}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl className={classes.filterControl}>
                    <InputLabel id="driver-stats-has-ridden-label">{t('secret_stats.filters.has_ridden')}</InputLabel>
                    <Select
                        labelId="driver-stats-has-ridden-label"
                        value={query.hasRidden}
                        onChange={handleHasRiddenChange}
                    >
                        <MenuItem value="">{t('secret_stats.filters.has_ridden_all')}</MenuItem>
                        <MenuItem value="true">{t('secret_stats.filters.has_ridden_yes')}</MenuItem>
                        <MenuItem value="false">{t('secret_stats.filters.has_ridden_no')}</MenuItem>
                    </Select>
                </FormControl>
                <TextField
                    className={classes.searchControl}
                    label={t('secret_stats.filters.search')}
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="disabled" />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    startIcon={<GetAppIcon />}
                    onClick={() => setExportOpen(true)}
                >
                    {t('secret_stats.export.button')}
                </Button>
                <FormControlLabel
                    className={classes.fullViewToggle}
                    control={
                        <Switch
                            color="secondary"
                            checked={fullView}
                            onChange={(event) => setFullView(event.target.checked)}
                        />
                    }
                    label={t('secret_stats.filters.full_view')}
                />
            </div>
            <FastTabs tabs={tabs} onChange={setActiveTab} />
            <Dialog open={exportOpen} onClose={() => !exporting && setExportOpen(false)}>
                <DialogTitle>
                    {t('secret_stats.export.title', { period: t(`secret_stats.tabs.${PERIODS[activeTab]}`) })}
                </DialogTitle>
                <DialogContent>
                    <RadioGroup
                        value={exportMode}
                        onChange={(event) => setExportMode(event.target.value as 'all' | 'range')}
                    >
                        <FormControlLabel
                            value="all"
                            control={<Radio color="secondary" />}
                            label={t('secret_stats.export.all', {
                                total: statsByPeriod[PERIODS[activeTab]]?.total ?? '…',
                            })}
                        />
                        <FormControlLabel
                            value="range"
                            control={<Radio color="secondary" />}
                            label={t('secret_stats.export.range')}
                        />
                    </RadioGroup>
                    <div className={classes.exportRangeFields}>
                        <TextField
                            className={classes.exportField}
                            type="number"
                            label={t('secret_stats.export.from')}
                            value={exportFrom}
                            disabled={exportMode !== 'range'}
                            onChange={(event) => setExportFrom(event.target.value)}
                        />
                        <TextField
                            className={classes.exportField}
                            type="number"
                            label={t('secret_stats.export.to')}
                            value={exportTo}
                            disabled={exportMode !== 'range'}
                            onChange={(event) => setExportTo(event.target.value)}
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button disabled={exporting} onClick={() => setExportOpen(false)}>
                        {t('secret_stats.export.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<GetAppIcon />}
                        disabled={exporting || !exportRange()}
                        onClick={handleExport}
                    >
                        {exporting ? t('secret_stats.export.in_progress') : t('secret_stats.export.button')}
                    </Button>
                </DialogActions>
            </Dialog>
        </App>
    );
};

export default withTranslation(['driver', 'common'])(DriverStatsPage);
